import React from 'react';
import { Layout, Row, Col, Card, Button } from 'antd';
import { EditFilled, UploadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import ScrollToTopOnMount from '../scroll_to_top_on_mount/ScrollToTopOnMount';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	siteSurveyTemplatesRestCrudThunksForBuyer,
	siteSurveyTemplatesRestCrudThunksForSupplier,
} from '../../thunks/site_survey_templates_thunks';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';
import {
	taskListsRestCrudThunksForBuyer,
	taskListsRestCrudThunksForSupplier,
} from '../../thunks/task_lists_thunks';
import TaskListRO from '../task_list/TaskListRO';
import { ExportToCsv } from 'export-to-csv';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import FlatfileImporter from '../flatfile/FlatfileImporter';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import {
	REGEX_CONFIG_NAMES,
	regexRecordHook,
	requiredWithValueRecordHook,
	resetFieldWithValueRecordHook,
} from '../flatfile/faltfile_utils';
import {
	getSiteSurveyAnswerTypeDisplayName,
	isTaskListEnabled,
	SITE_SURVEY_ANSWER_TYPE,
	validateForDuplicateTitlesUnderSameGroup,
} from '../../utils/SiteSurveyUtils';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';

const { Content } = Layout;

require('./SiteSurveyTemplatesDetailDetailsPage.less');

const FLATFILE_FIELDS = [
	{
		label: 'Title',
		key: 'title',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Description',
		key: 'description',
		type: 'string',
	},
	{
		label: 'Group',
		key: 'groupTitle',
		type: 'string',
	},
	{
		label: 'Answer Type',
		key: 'questionType',
		type: 'enum',
		config: {
			options: [
				{ label: 'Multiple Choice', value: 'multipleChoice' },
				{ label: 'Free Text', value: 'textbox' },
				...(isTaskListEnabled() ? [{ label: 'Task List', value: 'taskList' }] : []),
			],
		},
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Answer Options',
		key: 'multipleChoicesWithLabel',
		type: 'string',
	},
];

const csvOptions = {
	fieldSeparator: ',',
	quoteStrings: '"',
	decimalSeparator: '.',
	showLabels: true,
	showTitle: false,
	title: 'Task List',
	useTextFile: false,
	useBom: true,
	headers: ['Title', 'Description', 'Group', 'Answer Type', 'Answer Options'],
};

class SiteSurveyTemplatesDetailDetailsPage extends React.Component<any, any> {
	constructor(props) {
		super(props);
		this.state = {
			initialLoading: true,
			addTaskModalVisible: false,
			uploadTaskModalVisible: false,
			editTaskListVisible: false,
		};
	}

	componentDidMount() {
		const {
			getSiteSurveyTemplate,

			match,
		} = this.props;
		const siteSurveyTemplateId = match.params.id;
		getSiteSurveyTemplate(siteSurveyTemplateId).finally(() => {
			this.setState({ initialLoading: false });
		});
	}

	onUploadTaskList = () => {
		this.setState({ uploadTaskModalVisible: true });
	};

	onUploadTaskModalClose = () => {
		this.setState({ uploadTaskModalVisible: false });
	};

	showEditTaskList = () => this.setState({ editTaskListVisible: true });

	_onEditTaskListComplete = () => this.setState({ editTaskListVisible: false });

	getSourceDataForSiteSurveyTemplate = (sst) => {
		const tasks = nullSafeGetOrElse('taskList.tasks', sst, []);
		const formattedTasks = tasks.map((task) => ({
			...task,
			multipleChoicesWithLabel: task.multipleChoicesWithLabel
				? task.multipleChoicesWithLabel
						.map((choice) => `${choice.value}${choice.description ? `:${choice.description}` : ''}`)
						.join(',')
				: undefined,
		}));
		return formattedTasks;
	};

	onDownloadTaskList = () => {
		const { siteSurveyTemplate } = this.props;
		const tasks = nullSafeGetOrElse('taskList.tasks', siteSurveyTemplate, []);
		const formattedTasks = tasks.map((task) => ({
			title: task.title,
			description: task.description || '',
			group: task.groupTitle || '',
			questionType: getSiteSurveyAnswerTypeDisplayName(task.questionType),
			multipleChoices: task.multipleChoicesWithLabel
				? task.multipleChoicesWithLabel
						.map((choice) => `${choice.value}${choice.description ? `:${choice.description}` : ''}`)
						.join(',')
				: '',
		}));
		const csvExporter = new ExportToCsv({ ...csvOptions, filename: siteSurveyTemplate.title });
		csvExporter.generateCsv(formattedTasks);
	};

	onDataLoad = (results) => {
		const { match, siteSurveyTemplate, getSiteSurveyTemplate, updateTaskList } = this.props;
		const siteSurveyTemplateId = match.params.id;
		return new Promise((resolve, reject) => {
			const newTasks = results.validData.map((task) => ({
				...task,
				multipleChoicesWithLabel: task.multipleChoicesWithLabel
					? task.multipleChoicesWithLabel.split(',').map((choice) => {
							const parts = choice.split(':');
							return {
								value: parts[0],
								...(parts.length > 1 && { description: parts[1] }),
							};
					  })
					: undefined,
			}));
			const newTaskList = { ...siteSurveyTemplate.taskList, tasks: newTasks };
			updateTaskList(newTaskList)
				.then(() => {
					resolve('Task Lists updated sucessfully');
					getSiteSurveyTemplate(siteSurveyTemplateId);
				})
				.catch((err) => reject(err));
		});
	};

	recordHook = (record) => {
		const updatedRecord = regexRecordHook([REGEX_CONFIG_NAMES.MULTIPLE_CHOICE_VALUES])(record);
		const updatedWithRequiredHook = requiredWithValueRecordHook(
			['multipleChoicesWithLabel'],
			'questionType',
			['multipleChoice']
		)(updatedRecord);
		// return updatedWithRequiredHook;
		return resetFieldWithValueRecordHook(['multipleChoicesWithLabel'], '', 'questionType', [
			SITE_SURVEY_ANSWER_TYPE.FREE_TEXT,
			SITE_SURVEY_ANSWER_TYPE.TASK_LIST,
		])(updatedWithRequiredHook);
	};

	render() {
		const { siteSurveyTemplate, userType, siteSurveyTemplatesFetching } = this.props;

		return this.state.initalLoading || siteSurveyTemplatesFetching ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="siteSurveyTemplatesDetailDetailsPage">
				<LogOnMountWithStandardEventProperties
					eventType={`visited ${userType} site survey template detail page`}
				/>
				<ScrollToTopOnMount />
				<Row gutter={16}>
					{siteSurveyTemplate.description ? (
						<Col span={24} className="rowSpacing">
							<Card>
								<div className="siteSurveyTemplatesDetailDetailsPage__descSection">
									<div className="siteSurveyTemplatesDetailDetailsPage__descSection">
										{siteSurveyTemplate.description}
									</div>
								</div>
							</Card>
						</Col>
					) : null}
					<Col span={24} className="rowSpacing">
						<Card
							title="Tasks"
							extra={
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES}>
									<div>
										<Button onClick={this.showEditTaskList}>
											<EditFilled /> Edit Task List
										</Button>
										{this.state.editTaskListVisible ? (
											<FlatfileImporter
												dataLoadComplete={this.state.editTaskListVisible}
												type="site_survey_template"
												fields={FLATFILE_FIELDS}
												data={this.getSourceDataForSiteSurveyTemplate(siteSurveyTemplate)}
												onComplete={this._onEditTaskListComplete}
												onDataLoad={this.onDataLoad}
												recordHook={this.recordHook}
												validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
											/>
										) : null}
										<Button
											type="primary"
											style={{ marginLeft: '24px' }}
											onClick={this.onUploadTaskList}
										>
											<UploadOutlined />
											Replace Task List
										</Button>
									</div>
								</AccessPermissionChecker>
							}
						>
							<TaskListRO tasks={nullSafeGetOrElse('taskList.tasks', siteSurveyTemplate, [])} />
						</Card>
					</Col>
				</Row>
				{this.state.uploadTaskModalVisible ? (
					<UploadWithSampleModal
						type="site_survey_template_upload"
						entityName="task"
						title="Replace Task List"
						downloadText="You can choose to download the existing list, add new tasks and upload! Do you want to download?"
						sampleFileText="Download task list"
						onCancel={this.onUploadTaskModalClose}
						onDownloadSample={this.onDownloadTaskList}
						fields={FLATFILE_FIELDS}
						onDataLoad={this.onDataLoad}
						recordHook={this.recordHook}
						showWarningMessage
						validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
					/>
				) : null}
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	onSuccess: ownProps.onSuccess,
	siteSurveyTemplatesFetching: state.site_survey_templates.fetching,
	siteSurveyTemplate: state.site_survey_templates.detail,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	isLoading: state.site_survey_templates.updating || state.site_survey_templates.fetching,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getSiteSurveyTemplate: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveyTemplatesRestCrudThunksForSupplier.readOne(id)
				: siteSurveyTemplatesRestCrudThunksForBuyer.readOne(id)
		),
	updateTaskList: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? taskListsRestCrudThunksForSupplier.update(entity)
				: taskListsRestCrudThunksForBuyer.update(entity)
		),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(SiteSurveyTemplatesDetailDetailsPage as any)
	) as any
);
